import { OnboardingSubscriptionOrderState } from '@houzy/schemas/partner-cockpit/onboarding';
import { SubscriptionPaymentMethod } from '@houzy/schemas/partner-management/partner-subscription-management';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Link as RouterLink } from '@tanstack/react-router';
import type { ReactElement, ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { formatSwissAmount } from '../../../lib/formatSwissAmount';
import { stateColor } from '../../../theme';
import { CheckoutSectionCard } from '../../subscriptionCheckout/components/CheckoutSectionCard';
import { orderQueryOptions } from '../api/subscriptionApi';

const NEXT_STEPS = [
    { key: 'VALIDATION', icon: <CheckCircleOutlineIcon />, current: true },
    { key: 'PAYMENT', icon: <ReceiptLongOutlinedIcon />, current: false },
    { key: 'ACTIVATION', icon: <RocketLaunchOutlinedIcon />, current: false },
] as const;

export function SubscriptionConfirmedStep(): ReactElement | null {
    const { t } = useTranslation();
    const { data: order } = useSuspenseQuery(orderQueryOptions());

    // The route redirects every other state away, so this is here to narrow the order to the confirmed variant.
    if (order.state !== OnboardingSubscriptionOrderState.Confirmed) {
        return null;
    }

    const billedTo = order.invoiceDetails?.companyName ?? order.greetingName;

    return (
        <Stack spacing={4}>
            <Stack spacing={1}>
                <Typography variant="h4">{t('ONBOARDING.SUBSCRIPTION.CONFIRMED.TITLE')}</Typography>
                <Typography variant="body2" color="text.secondary">
                    <Trans
                        i18nKey={
                            order.paymentMethod === SubscriptionPaymentMethod.Invoice
                                ? 'ONBOARDING.SUBSCRIPTION.CONFIRMED.SUBTITLE_INVOICE'
                                : 'ONBOARDING.SUBSCRIPTION.CONFIRMED.SUBTITLE_CARD'
                        }
                        values={{ name: billedTo }}
                        components={{ strong: <Typography component="span" variant="subtitle2" /> }}
                    />
                </Typography>
            </Stack>

            <CheckoutSectionCard title={t('CHECKOUT.PLAN.TITLE')}>
                <Stack spacing={1.5}>
                    <SummaryRow
                        label={t('ONBOARDING.SUBSCRIPTION.CONFIRMED.PLAN')}
                        value={<Typography variant="subtitle1">{order.plan.name}</Typography>}
                    />
                    <SummaryRow
                        label={t('ONBOARDING.SUBSCRIPTION.CONFIRMED.TOTAL', {
                            months: order.plan.commitmentLengthInMonths,
                        })}
                        value={
                            <Stack sx={{ alignItems: 'flex-end' }}>
                                <Typography variant="subtitle1">
                                    {t('CHECKOUT.AMOUNT', {
                                        amount: formatSwissAmount(order.plan.commitmentTotal),
                                    })}
                                </Typography>
                                <Typography variant="caption" color="text.disabled">
                                    {t('CHECKOUT.PRICING.AMOUNT_INCL_VAT', {
                                        amount: formatSwissAmount(order.plan.commitmentTotalInclVat),
                                    })}
                                </Typography>
                            </Stack>
                        }
                    />

                    {order.billingAddress && (
                        <>
                            <Divider />
                            <SummaryRow
                                label={t('ONBOARDING.SUBSCRIPTION.CONFIRMED.INVOICE_TO')}
                                value={
                                    <Stack sx={{ alignItems: 'flex-end' }}>
                                        <Typography variant="subtitle2">{order.billingAddress.companyName}</Typography>
                                        {order.invoiceContactName && (
                                            <Typography variant="body2" color="text.secondary">
                                                {order.invoiceContactName}
                                            </Typography>
                                        )}
                                        <Typography variant="body2" color="text.secondary">
                                            {order.billingAddress.line1}, {order.billingAddress.zip}{' '}
                                            {order.billingAddress.city}
                                        </Typography>
                                    </Stack>
                                }
                            />
                        </>
                    )}
                </Stack>
            </CheckoutSectionCard>

            <CheckoutSectionCard title={t('ONBOARDING.SUBSCRIPTION.CONFIRMED.NEXT_TITLE')}>
                <Stack spacing={2}>
                    {NEXT_STEPS.map((step) => (
                        <Stack key={step.key} direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
                            <Avatar
                                sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: (theme) =>
                                        stateColor(theme, 'focus', step.current ? 'secondary' : 'primary'),
                                    color: step.current ? 'success.main' : 'action.active',
                                }}>
                                {step.icon}
                            </Avatar>
                            <Stack sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle2">
                                    {t(`ONBOARDING.SUBSCRIPTION.CONFIRMED.NEXT.${step.key}.TITLE`)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t(`ONBOARDING.SUBSCRIPTION.CONFIRMED.NEXT.${step.key}.BODY`)}
                                </Typography>
                            </Stack>
                        </Stack>
                    ))}
                </Stack>
            </CheckoutSectionCard>

            <Button variant="contained" size="large" fullWidth component={RouterLink} to="/">
                {t('ONBOARDING.SUBSCRIPTION.CONFIRMED.VISIT_DASHBOARD')}
            </Button>
        </Stack>
    );
}

function SummaryRow({ label, value }: { label: string; value: ReactNode }): ReactElement {
    return (
        <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
                {label}
            </Typography>
            {value}
        </Stack>
    );
}
